<section class="toolbar">
	<lvr-database-column-selector
		[options]="columnOptions()"
		[selectedColumnIds]="selectedColumnIds()"
		[applying]="columnSelectorLocked()"
		(applySelection)="applyColumns($event)"
	></lvr-database-column-selector>
	<div class="toolbar-actions">
		<lvr-database-reference-date-filter
			#globalReferenceDate
			variant="global"
			[value]="referenceDate()"
			[disabled]="!referenceDateAvailable() || controlsLocked()"
			(valueChanged)="setReferenceDate($event)"
		/>
		<button
			type="button"
			class="date-range-preset"
			aria-haspopup="menu"
			[class.is-placeholder]="rangePresetButtonKey() === rangePresetPlaceholderKey"
			[matMenuTriggerFor]="rangePresetMenu"
			[disabled]="controlsLocked()"
		>
			<span class="date-range-preset-label">{{ rangePresetButtonKey() | transloco }}</span>
			<mat-icon>arrow_drop_down</mat-icon>
		</button>
		<mat-menu #rangePresetMenu="matMenu" class="range-preset-menu">
			@for (preset of rangePresets; track preset) {
				<button mat-menu-item type="button" (click)="applyRangePreset(preset)">
					<mat-icon>{{ rangePresetIcons[preset] }}</mat-icon>
					{{ rangePresetLabelKeys[preset] | transloco }}
				</button>
			}
		</mat-menu>
		<div class="date-range">
			<div class="date-range-field">
				<mat-icon class="date-range-prefix">calendar_month</mat-icon>
				<input
					class="date-range-input"
					[placeholder]="'database_view.assignments.toolbar.period' | transloco"
					[value]="selectedRangeLabel()"
					readonly
					[disabled]="controlsLocked()"
					(click)="rangePicker.open()"
					(focus)="rangePicker.open()"
				/>
				@if (rangeStart() || rangeEnd()) {
					<button
						matIconButton
						type="button"
						class="date-range-clear"
						[attr.aria-label]="'database_view.assignments.toolbar.reset' | transloco"
						[disabled]="controlsLocked()"
						(click)="clearDateRange($event)"
					>
						<mat-icon>close</mat-icon>
					</button>
				}
			</div>
			<mat-form-field class="sr-only-range">
				<mat-date-range-input [rangePicker]="rangePicker">
					<input
						matStartDate
						[value]="rangeStart()"
						[disabled]="controlsLocked()"
						(dateInput)="setRangeStart($event.value)"
						[placeholder]="'database_view.assignments.toolbar.from' | transloco"
					/>
					<input
						matEndDate
						[value]="rangeEnd()"
						[disabled]="controlsLocked()"
						(dateInput)="setRangeEnd($event.value)"
						[placeholder]="'database_view.assignments.toolbar.to' | transloco"
					/>
				</mat-date-range-input>
				<mat-date-range-picker #rangePicker></mat-date-range-picker>
			</mat-form-field>
		</div>
	</div>
	<div class="toolbar-buttons">
		<lvr-button
			variant="primary"
			[label]="'database_view.assignments.toolbar.reset' | transloco"
			[type]="'button'"
			(clicked)="resetAll()"
			icon="filter_alt_off"
		></lvr-button>
		<lvr-button
			variant="primary"
			[label]="'base.button.export' | transloco"
			[ariaLabel]="'base.button.export_csv' | transloco"
			[loading]="exporting()"
			[disabled]="!canExport()"
			[type]="'button'"
			(clicked)="export()"
			icon="ios_share"
		></lvr-button>
	</div>
</section>
<div class="progress-slot">
	<mat-progress-bar mode="indeterminate" [class.is-hidden]="!isPageLoading()"></mat-progress-bar>
</div>
<div class="table-wrap">
	<table
		mat-table
		[dataSource]="pageRows()"
		[style.--database-column-count]="displayedColumns.length"
		class="mat-elevation-z0"
	>
		@for (column of columns; track column.key) {
			<ng-container [matColumnDef]="column.key" [sticky]="isStickyColumn(column.key)">
				<th mat-header-cell *matHeaderCellDef>
					<div class="th-inline">
						<span class="th-label" [matTooltip]="column.labelKey | transloco">{{ column.labelKey | transloco }}</span>
						@if (column.sortField; as field) {
							<button
								matIconButton
								class="btn-tertiary"
								[class.sort-active]="sortField() === field"
								[disabled]="controlsLocked()"
								[matMenuTriggerFor]="sortMenu"
								(click)="setActiveSortField(field)"
							>
								<mat-icon>{{ sortIcon(field) }}</mat-icon>
							</button>
						}
					</div>

					@if (column.filter?.type === 'single') {
						@let fKey = singleFilterKey(column);
						<div class="th-filter-inline">
							@if (fKey && isSearchOnlySingleFilter(fKey)) {
								<input
									class="th-filter-input"
									(click)="$event.stopPropagation()"
									[value]="fKey ? filterSearch()[fKey] : ''"
									[class.is-filtered]="!!fKey && !!filterSearch()[fKey]"
									[disabled]="controlsLocked()"
									(input)="fKey && setFilterSearch(fKey, $any($event.target).value)"
									[placeholder]="'database_view.filters.placeholder_type_to_filter' | transloco"
								/>
							} @else {
								<lvr-database-multi-select-filter
									#filterPanel
									[panelWidth]="'auto'"
									[options]="fKey ? filterOptions(fKey) : []"
									[optionLabels]="fKey ? filterLabels(fKey) : {}"
									[selectedValues]="fKey ? filterValues()[fKey] : []"
									[applying]="controlsLocked()"
									[disabled]="isFilterUnavailable(fKey)"
									(selectionApplied)="fKey && applyMultiFilterSelection(fKey, $event, filterPanel)"
									(panelClosed)="fKey && onMultiFilterPanelClosed(fKey)"
								></lvr-database-multi-select-filter>
							}
						</div>
					} @else if (dateRangeFieldId(column); as fieldId) {
						<div class="th-filter-inline">
							<lvr-database-date-range-filter
								[start]="columnDateRange(fieldId).start"
								[end]="columnDateRange(fieldId).end"
								[disabled]="isDateRangeUnavailable(fieldId) || controlsLocked()"
								[labelKey]="column.labelKey"
								(rangeChanged)="setColumnDateRange(fieldId, $event)"
								(rangeCleared)="clearColumnDateRange(fieldId)"
							/>
						</div>
					} @else if (isReferenceDateColumn(column)) {
						<div class="th-filter-inline">
							<lvr-database-reference-date-filter
								variant="column"
								[value]="referenceDate()"
								[disabled]="!referenceDateAvailable() || controlsLocked()"
								(valueChanged)="setReferenceDate($event)"
								(openRequested)="globalReferenceDate.open()"
							/>
						</div>
					} @else {
						<div class="th-filter-inline"></div>
					}
				</th>

				<td mat-cell *matCellDef="let row">
					@if (column.key === 'ifd') {
						@if (hasIfdOverviewLink(row)) {
							<a
								class="cell-link"
								[routerLink]="['/desk/integration-service-office', row.ifdId]"
								[matTooltip]="row.ifd || '-'"
								>{{ row.ifd || '-' }}</a
							>
						} @else {
							<span class="cell-text" [matTooltip]="row.ifd || '-'">{{ row.ifd || '-' }}</span>
						}
					} @else if (column.key === 'advisor') {
						@if (hasAdvisorLink(row)) {
							<a
								class="cell-link"
								[routerLink]="['/desk/user', row.advisorObjectId]"
								[matTooltip]="row.advisor || '-'"
								>{{ row.advisor || '-' }}</a
							>
						} @else {
							<span class="cell-text" [matTooltip]="row.advisor || '-'">{{ row.advisor || '-' }}</span>
						}
					} @else if (column.key === 'clientName') {
						@if (hasClientLink(row)) {
							<a class="cell-link" [routerLink]="['/desk/client', row.clientId]" [matTooltip]="row.clientName">{{
								row.clientName
							}}</a>
						} @else {
							<span class="cell-text" [matTooltip]="row.clientName">{{ row.clientName }}</span>
						}
					} @else if (column.key === 'phase') {
						@if (hasAssignmentLink(row)) {
							<a class="cell-link" [routerLink]="getAssignmentDetailsLink(row)" [matTooltip]="row.phase">{{
								row.phase
							}}</a>
						} @else {
							<span class="cell-text" [matTooltip]="row.phase">{{ row.phase }}</span>
						}
					} @else if (column.key === 'assignmentNo') {
						@if (hasAssignmentLink(row)) {
							<a class="cell-link" [routerLink]="getAssignmentDetailsLink(row)" [matTooltip]="row.assignmentNo">{{
								row.assignmentNo
							}}</a>
						} @else {
							<span class="cell-text" [matTooltip]="row.assignmentNo">{{ row.assignmentNo }}</span>
						}
					} @else {
						<span class="cell-text" [matTooltip]="getCellValue(row, column.key)">{{
							getCellValue(row, column.key)
						}}</span>
					}
				</td>
			</ng-container>
		}

		<tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
		<tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
		<tr class="mat-row" *matNoDataRow>
			<td class="mat-cell no-data-cell" [attr.colspan]="displayedColumns.length">
				Keine Aufträge für die aktuelle Filterauswahl gefunden.
			</td>
		</tr>
	</table>
</div>

<div class="table-footer">
	<span class="total-results">{{ 'database_view.total_results' | transloco: { count: totalItems() } }}</span>
	<lvr-paginator
		[length]="totalItems()"
		[pageSize]="pageSize()"
		[pageIndex]="pageIndex()"
		[showFirstLastButtons]="true"
		[disabled]="controlsLocked()"
		preferenceKey="database-assignments-list"
		(page)="handlePageEvent($event)"
	></lvr-paginator>
</div>

<mat-menu #sortMenu="matMenu" class="sort-menu">
	<button
		mat-menu-item
		class="sort-menu-item"
		[disabled]="controlsLocked()"
		(click)="setSort(activeSortField(), 'asc')"
	>
		<span class="sort-menu-label">{{ sortLabel('asc') | transloco }}</span>
		<mat-icon class="sort-check" [class.is-hidden]="!isSortOptionActive(activeSortField(), 'asc')">check</mat-icon>
	</button>
	<button
		mat-menu-item
		class="sort-menu-item"
		[disabled]="controlsLocked()"
		(click)="setSort(activeSortField(), 'desc')"
	>
		<span class="sort-menu-label">{{ sortLabel('desc') | transloco }}</span>
		<mat-icon class="sort-check" [class.is-hidden]="!isSortOptionActive(activeSortField(), 'desc')">check</mat-icon>
	</button>
</mat-menu>
